/**
 * Contract Drafting Detail — Constants
 * Google Material color tokens
 */

// Status labels and colors — Google Material palette
export const STATUS_CONFIG = {
  NEW: {
    label: "New",
    color: "#1967d2",       // Google Blue 700
    background: "#e8f0fe",  // Google Blue 50
    icon: "add_circle",
  },
  INTAKE_IN_PROGRESS: {
    label: "Intake In Progress",
    color: "#e37400",       // Google Amber 700
    background: "#fff8e1",  // Google Amber 50
    icon: "hourglass_top",
  },
  READY_FOR_DRAFTING: {
    label: "Ready for Drafting",
    color: "#ea8600",       // Google Orange 700
    background: "#fef7e0",  // Google Orange 50
    icon: "edit",
  },
  DRAFTING_IN_PROGRESS: {
    label: "Drafting In Progress",
    color: "#f9ab00",       // Google Gold 600
    background: "#fef7e0",  // Google Gold 50
    icon: "autorenew",
  },
  DRAFT_GENERATED: {
    label: "Draft Generated",
    color: "#9334E6",       // Google Purple 700
    background: "#f3e8fd",  // Google Purple 50
    icon: "description",
  },
  REVIEW_IN_PROGRESS: {
    label: "Review In Progress",
    color: "#00796B",       // Teal 700
    background: "#E0F2F1",  // Teal 50
    icon: "rate_review",
  },
  REVIEWED: {
    label: "Reviewed",
    color: "#303F9F",        // Indigo 700
    background: "#E8EAF6",  // Indigo 50
    icon: "verified",
  },
  COMPLETED: {
    label: "Completed",
    color: "#137333",       // Google Green 700
    background: "#e6f4ea",  // Google Green 50
    icon: "check_circle",
  },
  NEEDS_ATTENTION: {
    label: "Needs Attention",
    color: "#d93025",       // Google Red 600
    background: "#fce8e6",  // Google Red 50
    icon: "error",
  },
};

// Field data type validation patterns
export const DATA_TYPE_PATTERNS = {
  TEXT: /.*/,
  DATE: /^\d{4}-\d{2}-\d{2}$/,
  NUMBER: /^-?\d+(\.\d+)?$/,
};

// File upload constraints
export const FILE_UPLOAD = {
  MAX_SIZE: 25 * 1024 * 1024, // 25 MB
  ALLOWED_TYPES: [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/plain",
  ],
  ALLOWED_EXTENSIONS: [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".txt"],
};

// Section titles
export const SECTION_TITLES = {
  REQUEST_INFO: "Request Information",
  CONTRACT_DETAILS: "Contract Details",
  SUPPORTING_DOCS: "Supporting Documents",
  DRAFTS: "Generated Drafts",
};

// Button labels
export const BUTTON_LABELS = {
  EDIT: "Edit",
  SAVE: "Save Changes",
  CANCEL: "Cancel",
  PREVIEW: "Preview Template",
  GENERATE: "Generate Draft",
  REQUEST_REVIEW: "Request Review",
  MARK_COMPLETE: "Mark Complete",
  UPLOAD: "Upload Documents",
  DOWNLOAD: "Download",
  DELETE: "Delete",
};

// Modal titles
export const MODAL_TITLES = {
  DELETE_DOCUMENT: "Delete Document",
  PREVIEW_TEMPLATE: "Contract Template Preview",
  GENERATE_DRAFT: "Generate Contract Draft",
  REQUEST_REVIEW: "Request Review",
  MARK_COMPLETE: "Mark as Complete",
  VALIDATION_ERROR: "Missing Required Fields",
};

// Toast messages
export const TOAST_MESSAGES = {
  LOAD_SUCCESS: "Contract details loaded successfully",
  LOAD_ERROR: "Failed to load contract details",
  SAVE_SUCCESS: "Changes saved successfully",
  SAVE_ERROR: "Failed to save changes",
  UPLOAD_SUCCESS: "Documents uploaded successfully",
  UPLOAD_ERROR: "Failed to upload documents",
  DELETE_SUCCESS: "Document deleted successfully",
  DELETE_ERROR: "Failed to delete document",
  GENERATE_SUCCESS: "Draft generated successfully",
  GENERATE_ERROR: "Failed to generate draft",
  REVIEW_SUCCESS: "Review request sent successfully",
  REVIEW_ERROR: "Failed to send review request",
  COMPLETE_SUCCESS: "Contract marked as complete",
  COMPLETE_ERROR: "Failed to mark contract as complete",
  VALIDATION_ERROR: "Please fill all required fields",
};

// API Endpoints (relative to base URL: /api/contract_draft/)
export const API_ENDPOINTS = {
  CONTRACT_REQUEST: (id: string) => `/contract_draft/contract-requests/${id}/`,
  DATAPOINTS: (id: string) => `/contract_draft/contract-requests/${id}/datapoints/`,
  CITATION_URL: (requestId: string, datapointId: string) => 
    `/contract_draft/contract-requests/${requestId}/datapoints/${datapointId}/citation-url/`,
  DOCUMENTS: (id: string) => `/contract_draft/contract-requests/${id}/documents/`,
  DRAFTS: (id: string) => `/contract_draft/contract-requests/${id}/drafts/`,
  UPLOAD_DOCUMENT: (id: string) => `/contract_draft/contract-requests/${id}/documents/upload/`,
  SUBMIT_DOCUMENTS: (id: string) => `/contract_draft/contract-requests/${id}/supporting-documents/process/`,
  DELETE_DOCUMENT: (id: string, docId: string) =>
    `/contract_draft/contract-requests/${id}/documents/${docId}/`,
  DOWNLOAD_DOCUMENT: (id: string, docId: string) =>
    `/contract_draft/contract-requests/${id}/documents/${docId}/download/`,
  TEMPLATE_PREVIEW: (id: string) =>
    `/contract_draft/contract-requests/${id}/template-preview/`,
  GENERATE_DRAFT: (id: string) =>
    `/contract_draft/contract-requests/${id}/generate-draft/`,
  DRAFT_STATUS: (id: string) =>
    `/contract_draft/contract-requests/${id}/draft-status/`,
  REQUEST_REVIEW: (id: string) =>
    `/contract_draft/contract-requests/${id}/request-review/`,
  MARK_COMPLETE: (id: string) =>
    `/contract_draft/contract-requests/${id}/mark-complete/`,
  RETRY: (id: string) =>
    `/contract_draft/contract-requests/${id}/retry/`,
};

// Polling intervals
export const POLLING = {
  DRAFT_STATUS_INTERVAL: 10000, // 10 seconds
  MAX_RETRIES: 60, // 10 minutes total (60 * 10s)
};

// Date format
export const DATE_FORMAT = "YYYY-MM-DD";

// Completion checklist items
export const COMPLETION_CHECKLIST = [
  "Contract has been reviewed and approved",
  "All required signatures have been obtained",
  "Contract has been filed in the appropriate location",
];
